// The things, drawn (gate C5), in the low-poly look on its one surface material: gathering nodes (a herb's leaf clump,
// a flower's stems and bells, an ore vein's rock with glowing crystals) that vanish when gathered and grow back up
// when they regrow; breakables drawn from their own pre-cut fragments' outside faces (a crate's planks, a pot's
// facets: the same pieces that fly), then the fragments posed from their rigid bodies, shrinking as they fade; the
// pickups popping out of a break in an arc and bobbing where they land; the sign, the door (swinging open) and the
// levers; the walker on the crowd as a villager figure. A view: it reads the sim and never writes it.
import * as THREE from "three";
import { facet, fragmentGeometry, merge, seeded, type Fragment } from "@rpg/meshkit";
import type { LowPolyLook } from "@rpg/look";
import type { CollisionWorld } from "@rpg/collision";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { Fight } from "../enemies/fight";
import { mountFigureSet, type FigureSet } from "../bodies/figures";
import { WALKER } from "./lab";
import type { Things } from "./module";

const C = (hex: string): THREE.Color => new THREE.Color(hex);

/** One flat-coloured faceted part. */
const part = (g: THREE.BufferGeometry, colour: string, seed: number, glow = 0): THREE.BufferGeometry => facet(g, { colour: C(colour), jitter: 0, tint: 0.08, rng: seeded(seed), glow });

/** A gathering node's two looks: its whole self, and what stays when it's gathered (a stub, a bare rock). */
function nodeGeometry(shape: string, colour: string, accent: string, seed: number): { full: THREE.BufferGeometry; bare: THREE.BufferGeometry | null } {
  const r = seeded(seed);
  if (shape === "herb") {
    const blades: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2 + r() * 0.5;
      const g = new THREE.ConeGeometry(0.07, 0.42 + r() * 0.2, 3).translate(0, 0.22, 0);
      g.rotateZ(0.35 + r() * 0.25).rotateY(a);
      blades.push(part(g, i % 2 ? colour : accent, seed + i));
    }
    return { full: merge(blades), bare: stubs(colour, seed) };
  }
  if (shape === "flower") {
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + r();
      const d = 0.08 + r() * 0.08;
      const h = 0.35 + r() * 0.2;
      parts.push(part(new THREE.CylinderGeometry(0.012, 0.016, h, 4).translate(Math.cos(a) * d, h / 2, Math.sin(a) * d), colour, seed + i));
      parts.push(part(new THREE.ConeGeometry(0.06, 0.09, 5).rotateX(Math.PI).translate(Math.cos(a) * d, h + 0.02, Math.sin(a) * d), accent, seed + 10 + i));
    }
    for (let i = 0; i < 3; i++) parts.push(part(new THREE.ConeGeometry(0.05, 0.2, 3).rotateZ(0.6).rotateY(i * 2.1).translate(0, 0.06, 0), colour, seed + 20 + i));
    return { full: merge(parts), bare: stubs(colour, seed) };
  }
  // ore: a rock (kept) and crystals (gathered)
  const rock = part(new THREE.IcosahedronGeometry(0.55, 0).scale(1.3, 0.75, 1).translate(0, 0.32, 0), colour, seed);
  const crystals: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + r() * 0.4;
    crystals.push(part(new THREE.OctahedronGeometry(0.11 + r() * 0.06, 0).scale(0.7, 1.6, 0.7).rotateZ(0.3 - r() * 0.6).translate(Math.cos(a) * 0.42, 0.55 + r() * 0.15, Math.sin(a) * 0.3), accent, seed + 30 + i, 0.6));
  }
  return { full: merge([rock, ...crystals]), bare: rock };
}

/** What a picked herb or flower leaves: a few cut stems on a patch of bare earth. */
function stubs(colour: string, seed: number): THREE.BufferGeometry {
  const r = seeded(seed + 7);
  const parts = [part(new THREE.CylinderGeometry(0.2, 0.24, 0.03, 7).translate(0, 0.015, 0), "#6b5236", seed + 40)];
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + r();
    parts.push(part(new THREE.CylinderGeometry(0.014, 0.018, 0.07, 4).translate(Math.cos(a) * 0.07, 0.05, Math.sin(a) * 0.07), colour, seed + 41 + i));
  }
  return merge(parts);
}

/** A pickup's little mesh by its item's look. */
function pickupGeometry(shape: string, colour: string, seed: number): THREE.BufferGeometry {
  const g =
    shape === "leaf"
      ? new THREE.ConeGeometry(0.1, 0.26, 3).scale(1, 1, 0.35).rotateZ(Math.PI / 2)
      : shape === "flower"
        ? new THREE.ConeGeometry(0.11, 0.12, 5).rotateX(Math.PI)
        : shape === "rock"
          ? new THREE.IcosahedronGeometry(0.11, 0)
          : shape === "plank"
            ? new THREE.BoxGeometry(0.34, 0.05, 0.11)
            : new THREE.TetrahedronGeometry(0.12, 0);
  // big enough to read at play distance, a little glow so it stands off the grass
  return part(g.scale(1.7, 1.7, 1.7), colour, seed, 0.35);
}

/** The things' view on the scene. */
export interface ThingsView {
  /** Counts for the handle: what is drawn now. */
  report(): { nodes: number; gathered: number; breakables: number; fragments: number; pickups: number; walker: boolean };
  /** Clear what the warm-up showed. */
  clearWarm(): void;
}

/** Mount the things' view; call it inside the valley look's warm-up (its `warm` hook), so its programs compile then. */
export function mountThingsView(o: {
  runtime: Runtime;
  scene: THREE.Scene;
  look: LowPolyLook;
  content: SpikeContent;
  things: () => Things;
  fight: () => Fight;
  collision: () => CollisionWorld;
  probe: (x: number, z: number, fromY: number) => number | null;
  /** Whether a placement is drawn (a streamed world: its chunk is live on the view tier); default every one. */
  shown?: (id: string) => boolean;
  /** The lab's walker on the crowd (default true; the streamed valley has none). */
  walker?: boolean;
}): ThingsView {
  const th = o.things();
  const c = th.content;
  const root = new THREE.Group();
  root.name = "things";
  o.scene.add(root);
  const mat = o.look.surface;

  // gathering nodes
  const nodes = new Map<string, { mesh: THREE.Mesh; bare: THREE.Mesh | null; grow: number }>();
  for (const p of c.placements.filter((x) => x.kind === "gatherable")) {
    const k = c.gatherKinds.get(p.piece)!;
    const g = nodeGeometry(k.look.shape, k.look.colour, k.look.accent, p.seed);
    const f = th.at(p.id);
    const mesh = new THREE.Mesh(g.full, mat);
    mesh.position.set(f[0], f[1], f[2]);
    mesh.rotation.y = p.yaw;
    mesh.name = `node:${p.id}`;
    root.add(mesh);
    let bare: THREE.Mesh | null = null;
    if (g.bare) {
      bare = new THREE.Mesh(g.bare, mat);
      bare.position.copy(mesh.position);
      bare.rotation.y = p.yaw;
      bare.visible = false;
      root.add(bare);
    }
    nodes.set(p.id, { mesh, bare, grow: 1 });
  }

  // breakables: their fragments' outside faces, merged (the intact prop), and each fragment's own mesh for the break
  const intact = new Map<string, THREE.Mesh>();
  const fragGeo = new Map<string, THREE.BufferGeometry[]>();
  for (const p of c.placements.filter((x) => x.kind === "breakable")) {
    const k = c.breakables.get(p.piece)!;
    const frs = th.fragmentsOf(p.id);
    const outer = C(k.colours.outer);
    const inner = C(k.colours.inner);
    const geos = frs.map((fr, i) => fragmentGeometry(fr, outer, inner, { seed: p.seed * 31 + i, tint: 0.1 }));
    fragGeo.set(p.id, geos);
    // the intact prop: every fragment's outside faces where they lie in the whole
    const whole = merge(frs.map((fr, i) => surfaceOnly(fr, geos[i]!)));
    const mesh = new THREE.Mesh(whole, mat);
    const f = th.at(p.id);
    mesh.position.set(f[0], f[1], f[2]);
    mesh.rotation.y = p.yaw;
    mesh.name = `breakable:${p.id}`;
    root.add(mesh);
    intact.set(p.id, mesh);
  }
  const flying = new Map<string, THREE.Mesh>();

  // interactables
  const props = new Map<string, { kind: string; moving: THREE.Object3D | null; amount: number; group: THREE.Group }>();
  for (const p of c.placements.filter((x) => x.kind === "interactable")) {
    const k = c.interactables.get(p.piece)!;
    const f = th.at(p.id);
    const g = new THREE.Group();
    g.position.set(f[0], f[1], f[2]);
    g.rotation.y = p.yaw;
    g.name = `interactable:${p.id}`;
    let moving: THREE.Object3D | null = null;
    if (k.kind === "sign") {
      // one mesh (one draw call): the post, the board and its two lines
      g.add(
        new THREE.Mesh(
          merge([
            part(new THREE.BoxGeometry(0.1, 1.1, 0.1).translate(0, 0.55, 0), "#7a5a3a", p.seed),
            part(new THREE.BoxGeometry(0.9, 0.5, 0.06).translate(0, 1.15, 0.05), "#c9a46a", p.seed + 1),
            part(new THREE.BoxGeometry(0.62, 0.05, 0.02).translate(0, 1.22, 0.09), "#6b4a2c", p.seed + 2),
            part(new THREE.BoxGeometry(0.5, 0.05, 0.02).translate(0, 1.08, 0.09), "#6b4a2c", p.seed + 3),
          ]),
          mat,
        ),
      );
    } else if (k.kind === "lever") {
      g.add(new THREE.Mesh(part(new THREE.BoxGeometry(0.5, 0.3, 0.35).translate(0, 0.15, 0), "#8a8478", p.seed), mat));
      const pivot = new THREE.Group();
      pivot.position.set(0, 0.3, 0);
      pivot.add(new THREE.Mesh(part(new THREE.CylinderGeometry(0.03, 0.04, 0.75, 6).translate(0, 0.37, 0), "#5b4630", p.seed + 1), mat));
      pivot.add(new THREE.Mesh(part(new THREE.SphereGeometry(0.07, 6, 4).translate(0, 0.76, 0), "#c0563a", p.seed + 2), mat));
      g.add(pivot);
      moving = pivot;
    } else {
      // the door's slab on its hinge at the doorway's -x edge (in its own frame)
      const hinge = new THREE.Group();
      hinge.position.set(-0.58, 0, 0);
      hinge.add(new THREE.Mesh(part(new THREE.BoxGeometry(1.14, 2.08, 0.12).translate(0.58, 1.05, 0), "#9b6a3f", p.seed), mat));
      hinge.add(new THREE.Mesh(part(new THREE.BoxGeometry(0.1, 0.1, 0.2).translate(1.0, 1.0, 0), "#d9b45a", p.seed + 1), mat));
      g.add(hinge);
      moving = hinge;
    }
    root.add(g);
    props.set(p.id, { kind: k.kind, moving, amount: 0, group: g });
  }

  // pickups
  const pickupGeo = new Map<string, THREE.BufferGeometry>();
  for (const [id, row] of c.itemRows) pickupGeo.set(id, pickupGeometry(row.look.shape, row.look.colour, id.length * 97));
  const pickups = new Map<number, { g: THREE.Group; count: number }>();

  o.look.track(root);

  // the walker: a villager on the crowd
  const walker: FigureSet | null = o.walker === false ? null : mountFigureSet({
    runtime: o.runtime,
    scene: o.scene,
    look: o.look,
    content: o.content,
    bodies: () => o.fight().bodies,
    collision: o.collision,
    entries: () => {
      const a = o.fight().crowd.agent(WALKER.id);
      if (!a) return [];
      const sp = Math.hypot(a.vel[0], a.vel[2]);
      return [{ id: WALKER.id, card: "villager", pos: a.pos, yaw: sp > 0.1 ? Math.atan2(a.vel[0], a.vel[2]) : Math.PI, vel: a.vel }];
    },
    probe: o.probe,
    order: -13,
    name: "things: the walker",
  });

  let warm = true;
  const t0 = { real: 0 };
  o.runtime.on(
    "late",
    (frame) => {
      if (frame.mode === "play" && o.runtime.paused && !warm) return;
      t0.real += frame.realDt;
      const s = th.state();
      const col = o.collision();
      const dt = Math.min(0.1, frame.dt);
      // nodes: hidden while gathered, growing back after
      for (const [id, n] of nodes) {
        const gathered = s.gather.nodes[id]?.collected === true;
        if (gathered) n.grow = 0;
        else n.grow = Math.min(1, n.grow + dt / 0.6);
        const k = n.grow * n.grow * (3 - 2 * n.grow);
        const on = o.shown ? o.shown(id) : true;
        n.mesh.visible = on && n.grow > 0.001;
        n.mesh.scale.setScalar(Math.max(0.001, k));
        if (n.bare) n.bare.visible = on && (n.grow <= 0.001 || n.grow < 1);
      }
      // breakables: intact until broken (and, streamed, drawn only in a live chunk)
      for (const [id, m] of intact) m.visible = !s.broken[id] && (o.shown ? o.shown(id) : true);
      if (o.shown) for (const [id, pr] of props) pr.group.visible = o.shown(id);
      // fragments: posed from their bodies, shrinking as they fade
      const live = new Set<string>();
      for (const fr of s.fragments) {
        live.add(fr.body);
        let m = flying.get(fr.body);
        if (!m) {
          m = new THREE.Mesh(fragGeo.get(fr.of)![fr.index]!, mat);
          m.name = fr.body;
          root.add(m);
          flying.set(fr.body, m);
        }
        if (!col.has(fr.body)) continue;
        const pose = col.pose(fr.body);
        m.position.set(pose.position.x, pose.position.y, pose.position.z);
        m.quaternion.set(pose.rotation.x, pose.rotation.y, pose.rotation.z, pose.rotation.w);
        const fade = fr.settled === null ? 1 : Math.max(0, 1 - (s.t - fr.settled) / c.breakables.get(c.placements.find((p) => p.id === fr.of)!.piece)!.fade);
        m.scale.setScalar(Math.max(0.001, fade));
      }
      for (const [b, m] of flying)
        if (!live.has(b)) {
          m.removeFromParent();
          flying.delete(b);
        }
      // pickups: an arc out of the break, then a bob and a turn where they land
      const here = new Set<number>();
      for (const p of s.pickups.list) {
        here.add(p.id);
        let e = pickups.get(p.id);
        // a stack shows up to three of its item, fanned a little
        const shown = Math.min(3, p.count);
        if (!e || e.count !== shown) {
          e?.g.removeFromParent();
          const g = new THREE.Group();
          g.name = `pickup:${p.id}`;
          for (let i = 0; i < shown; i++) {
            const mi = new THREE.Mesh(pickupGeo.get(p.item)!, mat);
            mi.position.set(Math.sin(i * 2.1) * 0.13 * Math.min(1, i), i * 0.07, Math.cos(i * 2.1) * 0.13 * Math.min(1, i));
            mi.rotation.y = i * 1.1;
            g.add(mi);
          }
          root.add(g);
          e = { g, count: shown };
          pickups.set(p.id, e);
        }
        const m = e.g;
        const age = s.t - p.born;
        const k = Math.min(1, age / Math.max(0.05, c.pickups.delay));
        const x = p.from[0] + (p.pos[0] - p.from[0]) * k;
        const z = p.from[2] + (p.pos[2] - p.from[2]) * k;
        const y = p.from[1] + (p.pos[1] - p.from[1]) * k + Math.sin(k * Math.PI) * 0.8;
        const bob = k >= 1 ? 0.26 + Math.sin(t0.real * 3 + p.id) * 0.06 : 0.26;
        m.position.set(x, y + bob, z);
        m.rotation.y = t0.real * 1.6 + p.id;
      }
      for (const [id, e] of pickups)
        if (!here.has(id)) {
          e.g.removeFromParent();
          pickups.delete(id);
        }
      // doors swing, levers tilt
      for (const [id, pr] of props) {
        if (!pr.moving) continue;
        const on = pr.kind === "door" ? s.doors[id] === true : s.levers[id] === true;
        pr.amount += ((on ? 1 : 0) - pr.amount) * Math.min(1, dt * 8);
        if (pr.kind === "door") pr.moving.rotation.y = -pr.amount * 1.45;
        else pr.moving.rotation.x = -0.7 + pr.amount * 1.4;
      }
    },
    { order: -12, name: "things: view" },
  );

  return {
    report: () => {
      const s = th.state();
      return {
        nodes: [...nodes.values()].filter((n) => n.mesh.visible).length,
        gathered: Object.values(s.gather.nodes).filter((n) => n.collected).length,
        breakables: [...intact.values()].filter((m) => m.visible).length,
        fragments: flying.size,
        pickups: pickups.size,
        walker: walker ? walker.report().standing > 0 : false,
      };
    },
    clearWarm: () => {
      warm = false;
    },
  };
}

/** A fragment's outside faces only (the intact prop is drawn from these). */
function surfaceOnly(fr: Fragment, g: THREE.BufferGeometry): THREE.BufferGeometry {
  const keep = fr.surface.map((s, i) => (s ? i : -1)).filter((i) => i >= 0);
  const out = new THREE.BufferGeometry();
  for (const name of Object.keys(g.attributes)) {
    const a = g.getAttribute(name) as THREE.BufferAttribute;
    const n = a.itemSize;
    const arr = new Float32Array(keep.length * 3 * n);
    keep.forEach((t, j) => arr.set((a.array as Float32Array).subarray(t * 3 * n, (t + 1) * 3 * n), j * 3 * n));
    out.setAttribute(name, new THREE.BufferAttribute(arr, n));
  }
  // placed where the fragment sits in the whole
  out.translate(fr.centre[0], fr.centre[1], fr.centre[2]);
  return out;
}

